import { useEffect, useRef } from "react";
import { Animated, Easing, Platform } from "react-native";

const USE_NATIVE_DRIVER = Platform.OS !== "web";

interface PulseRingProps {
    color: string;
    delay?: number;
    inset?: number;
    width?: number;
    /** When true, render a static (non-animated) ring at low opacity. */
    reduced: boolean;
}

/**
 * One concentric pulse ring. Animates scale 1→1.9 + opacity 0.85→0 on a
 * 1800ms loop. Reduced-motion users see a static ring at 0.25 opacity
 * (the loop never starts). The opacity Animated.Value is initialized to
 * `reduced ? 0.25 : 0.85` — but importantly the parent only renders this
 * component AFTER `useResolvedReducedMotion().resolved === true`, so the
 * initial value reflects the user's actual setting (no stale capture).
 */
function PulseRing({ color, delay = 0, inset = -8, width = 2.5, reduced }: PulseRingProps) {
    const scale = useRef(new Animated.Value(1)).current;
    const opacity = useRef(new Animated.Value(reduced ? 0.25 : 0.85)).current;

    useEffect(() => {
        if (reduced) {
            return;
        }
        const loop = Animated.loop(
            Animated.parallel([
                Animated.sequence([
                    Animated.delay(delay),
                    Animated.timing(scale, {
                        toValue: 1.9,
                        duration: 1800,
                        easing: Easing.out(Easing.ease),
                        useNativeDriver: USE_NATIVE_DRIVER
                    })
                ]),
                Animated.sequence([
                    Animated.delay(delay),
                    Animated.timing(opacity, {
                        toValue: 0,
                        duration: 1800,
                        easing: Easing.out(Easing.ease),
                        useNativeDriver: USE_NATIVE_DRIVER
                    })
                ])
            ])
        );
        loop.start();
        return () => loop.stop();
    }, [scale, opacity, delay, reduced]);

    return (
        <Animated.View
            pointerEvents="none"
            style={{
                position: "absolute",
                top: inset,
                left: inset,
                right: inset,
                bottom: inset,
                borderRadius: 999,
                borderWidth: width,
                borderColor: color,
                transform: [{ scale }],
                opacity
            }}
        />
    );
}

interface Props {
    reduced: boolean;
}

/**
 * 3 concentric pulse rings around the FAB. Used by IdleFab when a text
 * field is focused. Caller must already have resolved reduced-motion.
 */
export function RingHalo({ reduced }: Props) {
    return (
        <>
            <PulseRing color="rgba(69,210,176,0.75)" inset={-6} width={3} reduced={reduced} />
            <PulseRing color="rgba(231,72,245,0.7)" inset={-1} width={2.5} reduced={reduced} />
            <PulseRing
                color="rgba(69,210,176,0.45)"
                inset={-14}
                width={2}
                delay={500}
                reduced={reduced}
            />
        </>
    );
}
